/**
 * This file contains the task protocol types that define the contract
 * between AI Core and backend services for task communication.
 */

import { AIAction } from './actions';
import { StructuredRequest } from './structured-requests';
import { StructuredResponse } from './structured-responses';

/**
 * Content type maps
 */

export type AIRequestType =
    | 'markdown' // Text input from user
    | 'structured' // Structured data input
    | 'file' // File/blob data
    | 'audio' // Audio stream data
    | 'done' // Marks end of requests for this task
    | 'interrupt'; // Request to interrupt current processing

// Maps request types to their content types
export type RequestContentMap = {
    markdown: MarkdownData;
    structured: StructuredRequest;
    done: null;
    interrupt: null;
    file: never; // @todo: Define when implementing file uploads
    audio: never; // @todo: Define when implementing audio streams
};

// Maps response types to their content types
export type ResponseContentMap = {
    markdown: MarkdownData;
    structured: StructuredResponse;
    audio: never; // @todo: Define when implementing audio
    action: AIAction;
    done: DoneResponseData;
    error: IHBError;
};

/**
 * Context information for task-level requests
 */
export interface AITaskRequestContext {
    /** Version information */
    versions?: Record<string, string>;

    /** Any additional context */
    [key: string]: unknown;
}

/**
 * Base server request types
 */

export interface AITaskRequest {
    /** Unique ID for an existing task, if continuing a task */
    task_id?: string;

    /** Type of AI feature being used */
    feature: string;

    /** Array of individual requests that make up this task */
    requests?: AIRequest[];

    /** Optional context information for the entire task */
    context?: AITaskRequestContext;
}

// Dynamically creates a discriminated union for each key in RequestContentMap to ensure type inference
type AIRequestContent = {
    [K in keyof RequestContentMap]: {
        type: K;
        content: RequestContentMap[K];
    };
}[keyof RequestContentMap]; // Converts the mapped type into a union

export type AIRequest = {
    /** Optional context information */
    context?: AIRequestContext;
} & AIRequestContent;

/**
 * Base server response types
 */

export interface AITaskResponse {
    /** Unique ID assigned to the task */
    ai_task_id: string;

    /** Feature type */
    feature: string;

    /** Current status */
    status: AITaskStatus;

    /** Communication protocol details - present on task creation or when reconnecting */
    protocol?: CommunicationProtocol;

    /** Optional initial responses */
    responses?: AIResponse[];
}

export interface AITaskListResponse {
    tasks: AITaskResponse[];
}

// Dynamically creates a discriminated union for each key in ResponseContentMap to ensure type inference
type AIResponseContent = {
    [K in keyof ResponseContentMap]: {
        type: K;
        content: ResponseContentMap[K];
    };
}[keyof ResponseContentMap]; // Converts the mapped type into a union

export type AIResponse = {
    /** Unique ID for this response */
    uid: string;

    /** Ordering index for this response */
    order: number;
    /** Response status */
    status?: AIResponseStatus;

    // The following fields are added if/when a response results in an object being created in DB
    object_id?: string;
    object_created_at?: string; // Date
    is_feedbackable?: boolean;
} & AIResponseContent;

export type AIResponseType =
    | 'markdown' // Markdown-formatted text
    | 'structured' // AI-generated suggestion
    | 'audio' // Audio data
    | 'action' // Client-side action
    | 'done' // Task completion
    | 'error'; // Error response

export type AIResponseStatus =
    | 'preparing' // Preparing response data
    | 'ready'; // Response data ready

export type AITaskStatus =
    | 'active' // Task is currently processing
    | 'completed' // Task completed successfully
    | 'error'; // Task encountered an error

export type CommunicationProtocolType = 'websocket' | 'test';

/**
 * Communication protocol configuration
 */

export interface CommunicationProtocol {
    /** Protocol type */
    type: CommunicationProtocolType;

    /** Protocol-specific configuration */
    data: unknown;
}

export type MarkdownData = string;

// Response data for task completion
export type DoneResponseData = {
    message_id: string;
    message_created_at: string; // date
};

// Error information structure
export interface IHBError {
    error_data: any;
    error_message: string;
    error_type: ErrorType;
    is_timeout: boolean;
    msg_to_user_only_on_non_prod?: string; // TODO: change name
}

export type ErrorType = (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES];

export const ERROR_TYPES = {
    UNSUPPORTED_AI_CAPABILITY: 'HBUnsupportedAiCapabilityError',
    ILLEGAL_PARAM: 'HBIllegalParamError',
    UNAUTHORIZED: 'HBUnauthorizedError',
    RATE_LIMIT: 'HBAIChatRateLimitError' // TODO: should be generic rate limit, not chat specific
};

/**
 * EVERYTHING BELOW THIS LINE IS A VAGUE INITIAL GUESS, WE'RE COLLECTING REQUIREMENTS, DO NOT USE AS A REFERENCE
 */

/**
 * @experimental
 */
export interface AIRequestContext {
    /** Current app route */
    route?: string;

    /** Visual context info */
    visual_context?: Record<string, unknown>;

    /** Any additional context */
    [key: string]: unknown;
}

/**
 * Create an `AIRequest` with type 'done' and no content, to
 * indicate a Done signal in the AI task protocol.
 */
export function aDoneAIRequest(): AIRequest {
    return {
        type: 'done',
        content: null
    };
}
